import {
    ImportRejection,
    ImportRejectionRowProblem,
} from "@common/schemas/domains/accounting/import-rejection.schema";
import { pluralize } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { DownloadIcon } from "@icons";
import {
    flaggedRowNumbers,
    flaggedRowProblems,
    useDownloadFlaggedRows,
} from "./useDownloadFlaggedRows";
import styles from "./ImportRejectionDetails.module.scss";

const ExampleRowsTable = ({
    rejection,
}: Readonly<{
    rejection: ImportRejection;
}>): React.ReactElement | null => {
    const exampleRows = rejection.exampleRows ?? [];
    if (exampleRows.length === 0) {
        return null;
    }
    const headers = exampleRows[0].headers;
    return (
        <table className={styles.exampleTable}>
            <thead>
                <tr>
                    <th>Row</th>
                    {headers.map((header, index) => (
                        <th key={`${header}-${index}`}>{header}</th>
                    ))}
                    <th>Problem</th>
                </tr>
            </thead>
            <tbody>
                {exampleRows.map((exampleRow) => (
                    <tr key={exampleRow.rowNumber}>
                        <td>{exampleRow.rowNumber}</td>
                        {headers.map((header, index) => (
                            <td
                                key={`${header}-${index}`}
                                title={exampleRow.cells[index] ?? ""}
                            >
                                {exampleRow.cells[index] ?? ""}
                            </td>
                        ))}
                        <td className={styles.problemCell}>
                            {exampleRow.problem}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

function remainingRowsLabel(rejection: ImportRejection): string | null {
    const shownCount = rejection.exampleRows?.length ?? 0;
    const totalCount = rejection.totalCount ?? shownCount;
    const remainingCount = totalCount - shownCount;
    if (remainingCount <= 0) {
        return null;
    }
    return `and ${remainingCount} more ${pluralize("row", remainingCount)}`;
}

const DownloadFlaggedRowsButton = ({
    documentId,
    fileName,
    rowNumbers,
    rowProblems,
}: {
    documentId: number;
    fileName?: string;
    rowNumbers: number[];
    rowProblems: ImportRejectionRowProblem[];
}) => {
    const { download, isDownloading } = useDownloadFlaggedRows({
        documentId,
        fileName,
        rowNumbers,
        rowProblems,
    });
    return (
        <Button
            type="button"
            variant="primary"
            size="sm"
            className={styles.downloadButton}
            disabled={isDownloading}
            onClick={() => download()}
            leftIcon={<DownloadIcon width={14} height={14} />}
        >
            Download flagged rows
        </Button>
    );
};

export const ImportRejectionDetails = ({
    rejection,
    trigger,
    documentId,
    fileName,
}: Readonly<{
    rejection: ImportRejection;
    trigger: React.ReactNode;
    documentId?: number;
    fileName?: string;
}>) => {
    const remaining = remainingRowsLabel(rejection);
    const rowNumbers = flaggedRowNumbers(rejection);
    const rowProblems = flaggedRowProblems(rejection);
    return (
        <Popover>
            <PopoverTrigger>
                <button type="button" className={styles.detailsTrigger}>
                    {trigger}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                side="bottom"
                className={styles.detailsContent}
            >
                <div className={styles.summary}>{rejection.summary}</div>
                <div className={styles.tableScroll}>
                    <ExampleRowsTable rejection={rejection} />
                </div>
                {remaining && (
                    <div className={styles.remaining}>{remaining}</div>
                )}
                {documentId != null && rowNumbers.length > 0 && (
                    <DownloadFlaggedRowsButton
                        documentId={documentId}
                        fileName={fileName}
                        rowNumbers={rowNumbers}
                        rowProblems={rowProblems}
                    />
                )}
                <div className={styles.guidance}>{rejection.guidance}</div>
            </PopoverContent>
        </Popover>
    );
};
